"use client";
import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { useRef, useState, useCallback, useEffect } from "react";
import { ClickAwayListener } from "@mui/material";
import { Card } from "../../Playground/Card/index.js";
import { PopoverButton } from "./PopoverButton.js";
import { StyledChevronDown, StyledPopper, Arrow } from "./styles.js";
import { ROLE_REGION } from "./types.js";
import { returnFocusToButtonElement, queryActionableElements, handleTabKeyDown, setFocusOnFirstElement } from "./util.js";
import { getDataTestId } from "../../Util/index.js";
const COMPONENT_NAME = "Popover";
const INVALID_PROP_ERROR = `${COMPONENT_NAME}-error: Either missing children or buttonText prop`;
const Popover = ({
  children,
  buttonText,
  buttonAriaLabel,
  roleRegion = false,
  hasArrow = false,
  hasChevronDownIcon = true,
  href,
  position = "bottom-start",
  labelledById,
  setFocus = "Container",
  sxButton,
  sxPopover,
  sxPopper,
  triggerAction = "onHover",
  onCloseCallback = () => null,
  uniqueId,
  target
}) => {
  const buttonRef = useRef(null);
  const popperContentRef = useRef();
  const lastActionable = useRef();
  const [arrowRef, setArrowRef] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [openedOnHover, setOpenedOnHover] = useState(false);
  const closePopover = useCallback(() => {
    setIsOpen(false);
    setOpenedOnHover(false);
    onCloseCallback();
  }, [setIsOpen]);
  const checkPopoverFocus = useCallback(
    (event) => {
      var _a;
      const visitExternalLinkText = document.querySelector(
        "#visit-external-link-text"
      );
      if (visitExternalLinkText) return;
      if (popperContentRef && !((_a = popperContentRef == null ? void 0 : popperContentRef.current) == null ? void 0 : _a.contains(
        event.relatedTarget
      )))
        return closePopover();
    },
    [closePopover]
  );
  const handleFocus = useCallback(
    (event) => {
      if (event.key === "Enter" || event.key === " " || event.key === "Tab" || event.key === "Shift") {
        return;
      }
      if (event.key === "Escape") {
        closePopover();
        returnFocusToButtonElement(buttonRef.current);
      }
    },
    [closePopover]
  );
  useEffect(() => {
    if (isOpen && popperContentRef && popperContentRef.current) {
      const actionableElements = queryActionableElements(
        popperContentRef.current
      );
      if (actionableElements.length > 0) {
        lastActionable.current = actionableElements[actionableElements.length - 1];
        lastActionable.current.addEventListener(
          "blur",
          checkPopoverFocus
        );
      }
      document.addEventListener("keydown", handleFocus);
    }
    return () => {
      document.removeEventListener("keydown", handleFocus);
    };
  }, [isOpen, handleFocus, checkPopoverFocus]);
  if (!children || !buttonText) {
    console.error(INVALID_PROP_ERROR);
    return null;
  }
  const openPopover = () => {
    setIsOpen(true);
  };
  const togglePopover = () => {
    setIsOpen((prevIsOpen) => {
      const isPopoverOpen = !prevIsOpen;
      if (!isPopoverOpen) {
        onCloseCallback();
      }
      return isPopoverOpen;
    });
  };
  const handleOpenHover = () => {
    setOpenedOnHover(true);
    openPopover();
  };
  const handlePopoverContentRef = (node) => {
    popperContentRef.current = node;
    if (node && setFocus === "First Actionable") {
      setFocusOnFirstElement(popperContentRef.current);
    }
  };
  const handleOnKeyDownButton = (event) => {
    const { key } = event;
    if (key === " " || key === "Enter") {
      event.preventDefault();
      togglePopover();
    }
  };
  const handleOnKeyDown = (event) => {
    handleTabKeyDown(event, popperContentRef, buttonRef, closePopover);
  };
  const handleClickAway = (event) => {
    var _a;
    if (buttonRef.current && ((_a = buttonRef == null ? void 0 : buttonRef.current) == null ? void 0 : _a.contains(event.target))) {
      return;
    }
    setIsOpen(false);
    onCloseCallback();
  };
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      PopoverButton,
      {
        "aria-expanded": isOpen,
        buttonText,
        buttonAriaLabel,
        isOpen,
        openedOnHover,
        href,
        target,
        ref: buttonRef,
        variant: "text-button",
        onClick: triggerAction === "onClick" || href ? togglePopover : triggerAction === "onHover" ? handleOpenHover : void 0,
        onMouseEnter: triggerAction === "onHover" ? handleOpenHover : void 0,
        onMouseLeave: triggerAction === "onHover" ? closePopover : void 0,
        onKeyDown: triggerAction === "onHover" || triggerAction === "onClick" ? handleOnKeyDownButton : void 0,
        sx: { padding: "4px", ...sxButton },
        children: hasChevronDownIcon && /* @__PURE__ */ jsx(StyledChevronDown, {})
      }
    ),
    /* @__PURE__ */ jsx(ClickAwayListener, { onClickAway: handleClickAway, children: /* @__PURE__ */ jsxs(
      StyledPopper,
      {
        "data-testid": getDataTestId(COMPONENT_NAME, uniqueId),
        disablePortal: true,
        open: isOpen,
        anchorEl: buttonRef.current,
        onMouseEnter: handleOpenHover,
        onMouseLeave: () => {
          const visitExternalLinkText = document.querySelector(
            "#visit-external-link-text"
          );
          if (visitExternalLinkText) return;
          closePopover();
        },
        onKeyDown: handleOnKeyDown,
        placement: position,
        role: roleRegion ? ROLE_REGION : void 0,
        "aria-labelledby": labelledById,
        sx: sxPopper,
        modifiers: [
          {
            name: "arrow",
            enabled: hasArrow,
            options: {
              element: arrowRef
            }
          }
        ],
        children: [
          hasArrow ? /* @__PURE__ */ jsx(
            Arrow,
            {
              component: "span",
              className: "arrow",
              ref: setArrowRef
            }
          ) : null,
          /* @__PURE__ */ jsx(
            Card,
            {
              ref: handlePopoverContentRef,
              sx: { padding: "24px", ...sxPopover },
              children
            }
          )
        ]
      }
    ) })
  ] });
};
Popover.displayName = COMPONENT_NAME;
export {
  Popover
};
